<script setup>
console.log('🚀 GM AI Messages Card: Initializing AI messages breakdown card...');

import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  totalAiMessages: {
    type: Number,
    required: true
  },
  captainMessages: {
    type: Number,
    required: true
  },
  agentBotMessages: {
    type: Number,
    required: true
  },
  description: {
    type: String,
    default: ''
  },
  color: {
    type: String,
    default: '#f59e0b'
  }
});

const { t } = useI18n();

console.log('📊 GM AI Messages Card: Props received:', {
  title: props.title,
  totalAiMessages: props.totalAiMessages,
  captainMessages: props.captainMessages,
  agentBotMessages: props.agentBotMessages
});

// Computed properties
const captainPercentage = computed(() => {
  if (props.totalAiMessages === 0) return 0;
  return ((props.captainMessages / props.totalAiMessages) * 100).toFixed(1);
});

const agentBotPercentage = computed(() => {
  if (props.totalAiMessages === 0) return 0;
  return ((props.agentBotMessages / props.totalAiMessages) * 100).toFixed(1);
});

const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

console.log('✅ GM AI Messages Card: Component setup completed');
</script>

<template>
  <div class="gm-ai-messages-card" :style="{ '--ai-color': color }">
    <div class="gm-ai-header">
      <div class="gm-ai-icon">
        🤖
      </div>
      <div class="gm-ai-title">
        {{ title }}
      </div>
    </div>
    
    <div class="gm-ai-content">
      <div class="gm-ai-total">
        {{ formatNumber(totalAiMessages) }}
      </div>
      
      <div v-if="description" class="gm-ai-description">
        {{ description }}
      </div>
      
      <div class="gm-ai-breakdown">
        <div class="gm-breakdown-item">
          <div class="gm-breakdown-label">
            <span class="gm-breakdown-icon">🧑‍💼</span>
            Captain
          </div>
          <div class="gm-breakdown-value">
            {{ formatNumber(captainMessages) }}
            <span class="gm-breakdown-percentage">({{ captainPercentage }}%)</span>
          </div>
        </div>
        
        <div class="gm-breakdown-item">
          <div class="gm-breakdown-label">
            <span class="gm-breakdown-icon">🤖</span>
            AgentBot
          </div>
          <div class="gm-breakdown-value">
            {{ formatNumber(agentBotMessages) }}
            <span class="gm-breakdown-percentage">({{ agentBotPercentage }}%)</span>
          </div>
        </div>
      </div>
      
      <div class="gm-ai-progress">
        <div class="gm-progress-bar">
          <div 
            class="gm-progress-captain" 
            :style="{ width: captainPercentage + '%' }"
            :title="`Captain: ${captainMessages} messages (${captainPercentage}%)`"
          ></div>
          <div 
            class="gm-progress-agentbot" 
            :style="{ width: agentBotPercentage + '%' }"
            :title="`AgentBot: ${agentBotMessages} messages (${agentBotPercentage}%)`"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gm-ai-messages-card {
  background: white;
  border-radius: 10px;
  padding: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
  border-left: 3px solid var(--ai-color);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.gm-ai-messages-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-left-color: var(--ai-color);
}

.gm-ai-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.gm-ai-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, var(--ai-color), var(--ai-color)dd);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.gm-ai-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  line-height: 1.3;
  flex: 1;
}

.gm-ai-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
}

.gm-ai-total {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.gm-ai-description {
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.4;
  margin-bottom: 0.5rem;
}

.gm-ai-breakdown {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: auto;
}

.gm-breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.gm-breakdown-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #374151;
}

.gm-breakdown-icon {
  font-size: 0.875rem;
}

.gm-breakdown-value {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #111827;
}

.gm-breakdown-percentage {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6b7280;
}

.gm-ai-progress {
  margin-top: 0.5rem;
}

.gm-progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
}

.gm-progress-captain {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  transition: width 0.3s ease;
}

.gm-progress-agentbot {
  height: 100%;
  background: linear-gradient(90deg, #8b5cf6, #7c3aed);
  transition: width 0.3s ease;
}

.gm-ai-messages-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--ai-color)08, transparent);
  border-radius: 0 12px 0 60px;
  pointer-events: none;
}

@media (max-width: 768px) {
  .gm-ai-messages-card {
    padding: 1rem;
  }
  
  .gm-ai-total {
    font-size: 1.75rem;
  }
  
  .gm-ai-icon {
    width: 36px;
    height: 36px;
    font-size: 1.125rem;
  }
  
  .gm-breakdown-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
  
  .gm-breakdown-value {
    align-self: flex-end;
  }
}
</style>
